import { API_ENDPOINTS } from "@/config/api";
import { fetchApi, reportUnauthorized } from "./api";

export interface SourceLogos {
  default: string | null;
  variants: Record<string, string>;
}

/** Slim trust summary embedded on source list responses. Score is 0–100. */
export interface SourceTrustSummary {
  /** Decimal may serialize as a JSON string (e.g. "87.50"). */
  score: number | string | null;
  verified_review_count: number;
  unverified_review_count: number;
  external_review_count: number;
  last_calculated_at: string;
}

export interface SourceWithStats {
  id: string;
  name: string;
  homepage_url: string;
  logos: SourceLogos;
  platform: string | null;
  product_count: number;
  variant_count: number;
  is_visible: boolean;
  last_scraped_at: string | null;
  created_at: string;
  updated_at: string | null;
  /** Null when the source has no trust score row. */
  trust_score?: SourceTrustSummary | null;
}

export interface SourceListResponse {
  sources: SourceWithStats[];
  total: number;
}

export interface Source {
  id: string;
  name: string;
  homepage_url: string;
  logos: SourceLogos;
}

export interface SourceDetails {
  id: string;
  platform: string | null;
  product_count: number;
  variant_count: number;
  is_visible: boolean;
  last_scraped_at: string | null;
  created_at: string;
  updated_at: string | null;
  scraper_batch_size?: number | null;
  scraper_batch_delay?: number | null;
  scraper_request_delay?: number | null;
  scraper_max_retries?: number | null;
  scrape_interval_hours?: number | null;
  scraper_throttle_auto?: boolean;
  scraper_clean_run_streak?: number;
}

export interface SourceCountResponse {
  count: number;
}

export interface SourceMappingResponse {
  mapping: Record<string, string>;
}

export interface ScraperSourceInfo {
  source: string;
  name: string;
  base_url: string;
  available: boolean;
}

export interface AvailableScrapersResponse {
  scrapers: ScraperSourceInfo[];
}

export interface SourceLogo {
  source: string;
  logos: SourceLogos;
}

export interface ActiveLogosResponse {
  logos: SourceLogo[];
  total: number;
}

export interface SourceSitemapHealth {
  source: string;
  platform: string;
  total_checks_24h: number;
  changes_detected_24h: number;
  errors_24h: number;
  latest_accuracy_score: number | null;
  avg_accuracy_score_30d: number | null;
  missed_changes_30d: number;
  false_positives_30d: number;
  last_checked_at: string | null;
  last_audited_at: string | null;
  reliability_rating:
    | "reliable"
    | "degraded"
    | "unreliable"
    | "unknown"
    | string;
}

export interface SitemapHealthSummary {
  sources: SourceSitemapHealth[];
  reliable_count: number;
  degraded_count: number;
  unreliable_count: number;
  unknown_count: number;
}

export async function getSources(
  includeHidden = true,
): Promise<SourceListResponse> {
  const search = new URLSearchParams();
  if (includeHidden) {
    search.set("include_hidden", "true");
  }
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.sources}?${query}`
    : API_ENDPOINTS.sources;
  return fetchApi<SourceListResponse>(url);
}

export async function getSource(sourceId: string): Promise<Source> {
  return fetchApi<Source>(API_ENDPOINTS.source(sourceId));
}

export async function getSourceDetails(
  sourceId: string,
): Promise<SourceDetails> {
  return fetchApi<SourceDetails>(API_ENDPOINTS.sourceDetails(sourceId));
}

export async function getSourcesCount(): Promise<SourceCountResponse> {
  return fetchApi<SourceCountResponse>(API_ENDPOINTS.sourcesCount);
}

export async function getSourcesMapping(
  includeHidden = true,
): Promise<SourceMappingResponse> {
  const search = new URLSearchParams();
  if (includeHidden) {
    search.set("include_hidden", "true");
  }
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.sourcesMapping}?${query}`
    : API_ENDPOINTS.sourcesMapping;
  return fetchApi<SourceMappingResponse>(url);
}

export async function getAvailableScrapers(): Promise<AvailableScrapersResponse> {
  return fetchApi<AvailableScrapersResponse>(API_ENDPOINTS.scrapersSources);
}

export async function getActiveSourceLogos(): Promise<ActiveLogosResponse> {
  return fetchApi<ActiveLogosResponse>(API_ENDPOINTS.scrapersLogosActive);
}

export async function getSourceLogo(sourceId: string): Promise<SourceLogo> {
  return fetchApi<SourceLogo>(API_ENDPOINTS.scrapersLogo(sourceId));
}

/** Variants the logo upload API accepts. Sent as the `variant` form field. */
export const LOGO_VARIANTS = [
  "default",
  "icon",
  "icon-white",
  "icon-black",
  "banner",
  "banner-white",
  "banner-black",
  "logo",
  "logo-white",
  "logo-black",
] as const;

export type LogoVariant = (typeof LOGO_VARIANTS)[number];

export function isLogoVariant(value: string): value is LogoVariant {
  return (LOGO_VARIANTS as readonly string[]).includes(value);
}

/**
 * FastAPI returns `detail` as a string for HTTPException, or a list of
 * `{ msg }` objects for request validation (422).
 */
export function formatLogoUploadError(
  detail: unknown,
  fallback: string,
): string {
  if (typeof detail === "string" && detail.length > 0) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.flatMap((item) => {
      if (typeof item === "string" && item.length > 0) return [item];
      if (
        typeof item === "object" &&
        item !== null &&
        "msg" in item &&
        typeof item.msg === "string" &&
        item.msg.length > 0
      ) {
        return [item.msg];
      }
      return [];
    });
    if (messages.length > 0) return messages.join("; ");
  }
  return fallback;
}

export async function uploadSourceLogo(
  sourceId: string,
  file: File,
  variant: LogoVariant = "icon",
): Promise<LogoUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("variant", variant);
  const response = await fetch(API_ENDPOINTS.scrapersLogoUpload(sourceId), {
    method: "POST",
    body: formData,
    credentials: "include",
  });
  if (!response.ok) {
    reportUnauthorized(
      API_ENDPOINTS.scrapersLogoUpload(sourceId),
      response.status,
    );
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: unknown = await response.json();
      if (
        typeof errorData === "object" &&
        errorData !== null &&
        "detail" in errorData
      ) {
        errorMessage = formatLogoUploadError(
          (errorData as Record<string, unknown>).detail,
          errorMessage,
        );
      }
    } catch {
      // ignore
    }
    throw new Error(errorMessage);
  }
  return response.json() as Promise<LogoUploadResponse>;
}

export async function getSitemapHealthSummary(): Promise<SitemapHealthSummary> {
  return fetchApi<SitemapHealthSummary>(API_ENDPOINTS.scrapersSitemapHealth);
}

export async function getSourceSitemapHealth(
  sourceId: string,
): Promise<SourceSitemapHealth> {
  return fetchApi<SourceSitemapHealth>(
    API_ENDPOINTS.scrapersSourceSitemapHealth(sourceId),
  );
}

export interface SourceWithStatsAdmin extends SourceWithStats {
  /** Only returned by GET /api/sources/admin/list */
  affiliate_url_template?: string | null;
  affiliate_program?: string | null;
  /** Decimal serialized as string by FastAPI, e.g. "2.50" */
  affiliate_commission_percent?: string | null;
  scraper_batch_size?: number | null;
  scraper_batch_delay?: number | null;
  scraper_request_delay?: number | null;
  scraper_max_retries?: number | null;
  scrape_interval_hours?: number | null;
  scraper_throttle_auto?: boolean;
  scraper_clean_run_streak?: number;
}

export interface SourceListAdmin {
  sources: SourceWithStatsAdmin[];
  total: number;
}

export async function listSourcesAdmin(
  includeHidden = true,
): Promise<SourceListAdmin> {
  const search = new URLSearchParams();
  if (includeHidden) search.set("include_hidden", "true");
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.sourcesAdminList}?${query}`
    : API_ENDPOINTS.sourcesAdminList;
  return fetchApi<SourceListAdmin>(url);
}

export interface SourceUpdateRequest {
  is_visible?: boolean;
  affiliate_url_template?: string | null;
  affiliate_program?: string | null;
  affiliate_commission_percent?: number | null;
  scraper_batch_size?: number | null;
  scraper_batch_delay?: number | null;
  scraper_request_delay?: number | null;
  scraper_max_retries?: number | null;
  scrape_interval_hours?: number | null;
  scraper_throttle_auto?: boolean;
}

export async function patchSource(
  sourceId: string,
  body: SourceUpdateRequest,
): Promise<SourceWithStats> {
  return fetchApi<SourceWithStats>(API_ENDPOINTS.source(sourceId), {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export interface LogoUploadResponse {
  source: string;
  logos: SourceLogos;
}
